/**
 * Form validation utilities
 *
 * Provides validation functions for each form section. Each validator returns
 * an object with field names as keys and error messages as values.
 */

import type { FormData, ValidationErrors } from '@ox/visits-ui/types';
import type { MediaFile } from '@ox/ox-brokerage-client';
import { ACCESS_ISSUES, COMBINED_QC_PHOTO_UPLOAD, COMPARABLE_1, COMPARABLE_GATE, CONDITION, EXTERIOR_CONDITION, QC_PHOTO_UPLOAD, LIGHTWEIGHT_INTERIOR_CONDITION, STANDARD_EXTERIOR_CONDITION, STANDARD_INTERIOR_CONDITION, MARKETABILITY_ASSESSMENT, PHOTO_UPLOAD, PROPERTY_ACCESS, PROPERTY_CONDITION, PROPERTY_DETAILS, QC_PROPERTY_ACCESS, SPARKLE_CLEAN, STANDARD_UTILITIES, UTILITIES, VALUATION, VIDEO_SUBMISSION } from '@ox/visits-ui/components/shared/Sections';
import { QC_COMBINED_PHOTO_SLOTS } from '@ox/visits-ui/config/qcPhotoSlots';

const isBlank = (value: string | string[] | number | undefined) =>
  value === undefined ||
  (typeof value === 'number' && isNaN(value)) ||
  (typeof value === 'string' && !value.trim().length) ||
  (Array.isArray(value) && value.length === 0);

/**
 * Parent visit type of an occupied walk (TXN-3408). The resident lets the agent in, so
 * there is no lockbox: "got in" carries no lockbox location or functional-key count.
 */
export const OCCUPIED_PREWALK_VISIT_TYPE = 'Occupied Prewalk';

export const isOccupiedWalk = (parentVisitType?: string | null) => parentVisitType === OCCUPIED_PREWALK_VISIT_TYPE;

/**
 * Validates Sections (Property Access)
 * Note: Basic required validation is handled by HTML5 required attribute
 *
 * @returns Object with validation errors (empty object if valid)
 *
 * @remarks
 * Conditional validation handled by showing/hiding fields based on user selection
 */
export default function validateSection(
  data: FormData,
  mediaFiles: MediaFile[],
  currentSection: number,
  parentVisitType?: string | null,
): ValidationErrors {
  const errors: ValidationErrors = {};

  const getMediaByType = (sectionNumber: number, mediaType: string) => {
    return mediaFiles.filter(
      (m) => m.sectionNumber === sectionNumber && m.mediaType === mediaType,
    );
  };

  switch (currentSection) {
    case PROPERTY_ACCESS:
      if (isBlank(data.were_able_to_access_property)) {
        errors.were_able_to_access_property = 'Please specify if you were able to access the property.';
      }
      if (data.were_able_to_access_property === 'Yes') {
        if (!isOccupiedWalk(parentVisitType)) {
          if (isBlank(data.number_of_functional_front_entry_keys)) {
            errors.number_of_functional_front_entry_keys = 'Please specify the number of functional front entry keys.';
          }
          if (isBlank(data.lockbox_location)) {
            errors.lockbox_location = 'Please specify the lockbox location.';
          }
        }
        if (isBlank(data.gated_community)) {
          errors.gated_community = 'Please specify if the property is in a gated community.';
        } else if (data.gated_community === 'Yes' && isBlank(data.gate_access_info)) {
          errors.gate_access_info = 'Please provide gate access information.';
        }
      }
      break;

    case QC_PROPERTY_ACCESS:
      if (isBlank(data.were_able_to_access_property)) {
        errors.were_able_to_access_property = 'Please specify if you were able to access the property.';
      } else if (data.were_able_to_access_property === 'Yes') {
        if (isBlank(data.lockbox_location)) {
          errors.lockbox_location = 'Please specify the lockbox location.';
        }
        if (isBlank(data.working_codes)) {
          errors.working_codes = 'Please specify which access code or codes gave you access.';
        }
        if (isBlank(data.squatters)) {
          errors.squatters = 'Please indicate if there are squatters present.';
        } else if (data.squatters === 'Yes' && isBlank(data.squatters_description)) {
          errors.squatters_description = 'Please describe the squatters present.';
        }
      }
      break;

    case ACCESS_ISSUES:
      if (isBlank(data.access_issue_details)) {
        errors.access_issue_details = 'Please provide details regarding the access issue.';
      }
      break;

    case PROPERTY_CONDITION:
      if (isBlank(data.installed_appliances)) {
        errors.installed_appliances = 'Please list the installed appliances.';
      }
      if (isBlank(data.appliances_need_replacement)) {
        errors.appliances_need_replacement = 'Please specify which appliances need replacement.';
      }
      if (isBlank(data.all_utilities_functioning)) {
        errors.all_utilities_functioning = 'Please indicate if all utilities are functioning.';
      }
      if (isBlank(data.sprinkler_system)) {
        errors.sprinkler_system = 'Please provide information about the sprinkler system.';
      }
      if (isBlank(data.odors_present)) {
        errors.odors_present = 'Please indicate if there is any odor present.';
      }
      if (isBlank(data.visible_roof_damage)) {
        errors.visible_roof_damage = 'Please indicate if there is any visible roof damage.';
      }
      if (isBlank(data.water_supplies_functional)) {
        errors.water_supplies_functional = 'Please indicate if all water supplies are functional.';
      }
      if (isBlank(data.visible_home_damage)) {
        errors.visible_home_damage = 'Please indicate if there is any visible home damage.';
      }
      if (isBlank(data.street_access_restrictions)) {
        errors.street_access_restrictions = 'Please provide details about any street access restrictions.';
      }
      if (isBlank(data.attic_crawl_space_accessible)) {
        errors.attic_crawl_space_accessible = 'Please indicate if the attic/crawl space is accessible.';
      }
      if (isBlank(data.any_home_damage)) {
        errors.any_home_damage = 'Please indicate if there is any home damage.';
      }
      break;

    case CONDITION:
      if (isBlank(data.odors_present)) {
        errors.odors_present = 'Please indicate if any odors are present.';
      }
      if (data.odors_present === 'Yes' && isBlank(data.odors_description)) {
        errors.odors_description = 'Please describe the odors present.';
      }
      if (isBlank(data.roof_damage)) {
        errors.roof_damage = 'Please indicate if there is any roof damage.';
      }
      if (data.roof_damage === 'Yes' && isBlank(data.roof_damage_description)) {
        errors.roof_damage_description = 'Please describe the roof damage.';
      }
      if (isBlank(data.dryrot_insects)) {
        errors.dryrot_insects = 'Please indicate if there is any dry rot or insect damage.';
      }
      if (data.dryrot_insects === 'Yes' && isBlank(data.dryrot_insects_description)) {
        errors.dryrot_insects_description = 'Please describe the dry rot or insect damage.';
      }
      if (isBlank(data.street_restrictions)) {
        errors.street_restrictions = 'Please indicate if there are any street restrictions.';
      }
      if (data.street_restrictions === 'Yes' && isBlank(data.street_restrictions_description)) {
        errors.street_restrictions_description = 'Please describe the street restrictions.';
      }
      if (isBlank(data.attic_crawlspace)) {
        errors.attic_crawlspace = 'Please indicate if the attic/crawlspace is accessible.';
      }
      if (data.attic_crawlspace === 'No' && isBlank(data.attic_crawlspace_description)) {
        errors.attic_crawlspace_description = 'Please describe why the attic/crawlspace is not accessible.';
      }
      if (isBlank(data.water_functioning)) {
        errors.water_functioning = 'Please indicate if the water is functioning.';
      }
      if (data.water_functioning === 'No' && isBlank(data.water_functioning_description)) {
        errors.water_functioning_description = 'Please describe the water functioning issues.';
      }
      break;

    case STANDARD_INTERIOR_CONDITION:
      // The QC Standard interior section hides the cleanliness description and the
      // temperature (hvac_working) input, so neither is validated here. Requiring
      // them blocked users on this page since the fields are never rendered.
      if (isBlank(data.cleanliness)) {
        errors.cleanliness = 'Please rate the cleanliness of the interior.';
      }
      if (isBlank(data.odors_present)) {
        errors.odors_present = 'Please indicate if any odors are present.';
      }
      if (data.odors_present === 'Yes') {
        if (isBlank(data.odors_description)) {
          errors.odors_description = 'Please describe the odors present.';
        }
      }
      // interior_damage (conditions/detractors) is optional.
      if (data.cleanliness === 'Poor') {
        if (isBlank(data.cleanliness_description)) {
          errors.cleanliness_description = 'Please describe what needs to be cleaned.';
        }
        if (getMediaByType(5, 'photo_cleanliness').length === 0) {
          errors.photo_cleanliness = 'Please upload a photo of the cleanliness issue(s).';
        }
      }
      if (isBlank(data.water_leak)) {
        errors.water_leak = 'Please indicate if there are signs of a water leak.';
      } else if (data.water_leak === 'Yes') {
        if (isBlank(data.water_leak_description)) {
          errors.water_leak_description = 'Please describe the water leak.';
        }
        if (getMediaByType(5, 'photo_water_leak').length === 0) {
          errors.photo_water_leak = 'Please upload a photo of the water leak.';
        }
      }
      break;

    case LIGHTWEIGHT_INTERIOR_CONDITION:
      if (isBlank(data.cleanliness)) {
        errors.cleanliness = 'Please rate the cleanliness of the interior.';
      }
      if (data.cleanliness === 'Poor' || data.cleanliness === 'Fair') {
        if (isBlank(data.cleanliness_description)) {
          errors.cleanliness_description = 'Please describe the cleanliness issues.';
        }
      }
      if (isBlank(data.interior_damage)) {
        errors.interior_damage = 'Please describe any specific interior conditions/detractors to the home.';
      }
      break;

    case EXTERIOR_CONDITION:
      if (isBlank(data.roof_condition)) {
        errors.roof_condition = 'Please rate the condition of the roof.';
      }
      if (data.roof_condition === 'Poor' || data.roof_condition === 'Fair') {
        if (isBlank(data.roof_condition_description)) {
          errors.roof_condition_description = 'Please describe the roof condition issues.';
        }
      }
      if (isBlank(data.exterior_paint)) {
        errors.exterior_paint = 'Please rate the condition of the exterior paint.';
      }
      if (data.exterior_paint === 'Poor' || data.exterior_paint === 'Fair') {
        if (isBlank(data.exterior_paint_description)) {
          errors.exterior_paint_description = 'Please describe the exterior paint/siding condition issues.';
        }
      }
      if (isBlank(data.landscaping)) {
        errors.landscaping = 'Please rate the condition of the landscaping.';
      }
      if (data.landscaping === 'Poor' || data.landscaping === 'Fair') {
        if (isBlank(data.landscaping_description)) {
          errors.landscaping_description = 'Please describe the landscaping/yard maintenance issues.';
        }
      }
      if (isBlank(data.sign_status)) {
        errors.sign_status = 'Please rate the condition of the sign or post.';
      }
      if (data.sign_status === 'No' && isBlank(data.sign_description)) {
        errors.sign_description = 'Please describe the sign or post condition issues.';
      }
      if (isBlank(data.visible_damage)) {
        errors.visible_damage = 'Please describe any specific exterior conditions/detractors to the home.';
      }
      break;

    case STANDARD_EXTERIOR_CONDITION:
      // Mirrors EXTERIOR_CONDITION minus the *_description fields, which the
      // combined (QC Standard) exterior section hides. Without this case the
      // section fell through to default and let users advance with blank fields.
      if (isBlank(data.roof_condition)) {
        errors.roof_condition = 'Please rate the condition of the roof.';
      }
      if (isBlank(data.exterior_paint)) {
        errors.exterior_paint = 'Please rate the condition of the exterior paint.';
      }
      if (isBlank(data.landscaping)) {
        errors.landscaping = 'Please rate the condition of the landscaping.';
      }
      if (isBlank(data.sign_status)) {
        errors.sign_status = 'Please indicate if the home has a valid sign installed.';
      }
      // Each assessment marked "Poor" requires a comment + photo of the issue.
      ([
        { key: 'roof_condition', desc: 'roof_condition_description', photo: 'photo_roof_condition', noun: 'roof condition' },
        { key: 'exterior_paint', desc: 'exterior_paint_description', photo: 'photo_exterior_paint', noun: 'exterior paint/siding' },
        { key: 'landscaping', desc: 'landscaping_description', photo: 'photo_landscaping', noun: 'landscaping/yard' },
      ] as const).forEach(({ key, desc, photo, noun }) => {
        if (data[key] === 'Poor') {
          if (isBlank(data[desc])) {
            errors[desc] = `Please describe the ${noun} issues.`;
          }
          if (getMediaByType(5, photo).length === 0) {
            errors[photo] = `Please upload a photo of the ${noun} issue(s).`;
          }
        }
      });
      // visible_damage (conditions/detractors) is optional.
      if (isBlank(data.mail_or_notices)) {
        errors.mail_or_notices = 'Please indicate if there is any mail or posted notices.';
      } else if (data.mail_or_notices === 'Yes' && getMediaByType(5, 'photo_mail_notices').length === 0) {
        errors.photo_mail_notices = 'Please upload a photo of the mail or posted notices.';
      }
      break;

    case PHOTO_UPLOAD:
      if (getMediaByType(5, 'photo_hvac').length === 0) {
        errors.photo_hvac = 'Please upload at least one HVAC photo.';
      }
      if (getMediaByType(5, 'photo_water_heater').length === 0) {
        errors.photo_water_heater = 'Please upload at least one Water Heater photo.';
      }
      if (getMediaByType(5, 'photo_lockbox').length === 0) {
        errors.photo_lockbox = 'Please upload at least one Lockbox Location photo.';
      }
      if (getMediaByType(5, 'photos_living_areas').length < 2) {
        errors.photos_living_areas = 'Please upload at least two Living Area photos.';
      }
      if (getMediaByType(5, 'photos_bathrooms').length < 1) {
        errors.photos_bathrooms = 'Please upload at least one Bathroom photo.';
      }
      if (getMediaByType(5, 'photos_bedrooms').length < 2) {
        errors.photos_bedrooms = 'Please upload at least two Bedroom photos.';
      }
      if (getMediaByType(5, 'photos_appliances').length < 3) {
        errors.photos_appliances = 'Please upload at least three Appliance photos.';
      }
      if (getMediaByType(5, 'photo_gas_range').length > 1) {
        errors.photo_gas_range = 'Please upload one Gas Range photo.';
      }
      if (getMediaByType(5, 'photo_running_water').length === 0) {
        errors.photo_running_water = 'Please upload at least one Running Water photo.';
      }
      if (getMediaByType(5, 'photos_sides').length < 2) {
        errors.photos_sides = 'Please upload two Side Property photos.';
      }
      if (getMediaByType(5, 'photos_roof').length < 2) {
        errors.photos_roof = 'Please upload at least two Roof photos.';
      }
      if (getMediaByType(5, 'photo_street_front').length === 0) {
        errors.photo_street_front = 'Please upload one Street View photo.';
      }
      break;

    case QC_PHOTO_UPLOAD:
      if (getMediaByType(5, 'photo_street_front').length === 0) {
        errors.photo_street_front = 'Please upload one Street View photo.';
      }
      if (getMediaByType(5, 'photos_interior').length < 1) {
        errors.photos_interior = 'Please upload at least one Interior photo.';
      }
      break;

    case COMBINED_QC_PHOTO_UPLOAD:
      if (getMediaByType(5, 'photo_street_front').length === 0) {
        errors.photo_street_front = 'Please upload one Street View photo.';
      }
      // Conditional-photo gates must be answered. Derived from the slot config
      // rather than restated here, so adding or removing a gated slot can't leave
      // validation enforcing a field the form no longer renders (or skipping one
      // it does). The `conditionLabel` filter is what marks a gate as answered
      // *inline in this section* — it excludes slots like `photo_for_sale_sign`,
      // whose `sign_status` gate is answered back in the exterior section.
      [
        ...new Set(
          QC_COMBINED_PHOTO_SLOTS.filter((slot) => slot.conditionLabel && slot.conditionField).map(
            (slot) => slot.conditionField as string,
          ),
        ),
      ].forEach((field) => {
        if (isBlank(data[field] as string)) {
          errors[field] = `Please indicate whether the home has a ${field.replace('has_', '')}.`;
        }
      });
      QC_COMBINED_PHOTO_SLOTS
        .filter((slot) => (slot.minFiles ?? 0) > 0 && slot.mediaType !== 'photo_street_front')
        // skip conditional slots whose gate isn't set to the expected value
        .filter((slot) => !slot.conditionField || data[slot.conditionField] === slot.conditionValue)
        .forEach((slot) => {
          const min = slot.minFiles ?? 0;
          if (getMediaByType(5, slot.mediaType).length < min) {
            errors[slot.mediaType] = min === 1
              ? `Please upload at least one ${slot.label} photo.`
              : `Please upload at least ${min} ${slot.label} photos.`;
          }
        });
      break;

    case VIDEO_SUBMISSION:
      if (getMediaByType(6, 'walkthrough_video').length === 0) {
        errors.walkthrough_video = 'Please upload at least one Walkthrough video.';
      }
      if (isBlank(data.property_comments)) {
        errors.property_comments = 'Please provide additional property comments or general feedback.';
      }
      break;

    case VALUATION:
      if (isBlank(data.confidence_level)) {
        errors.confidence_level = 'Please select your confidence level in the valuation.';
      }
      if (isBlank(data.estimated_value)) {
        errors.estimated_value = 'Please provide your estimated market value.';
      }
      if (isBlank(data.valuation_rationale)) {
        errors.valuation_rationale = 'Please provide your valuation rationale.';
      }
      break;

    case PROPERTY_DETAILS:
      if (isBlank(data.appliance_finishes)) {
        errors.apipliance_finishes = 'Please select the appliance finishes.';
      }
      if (isBlank(data.kitchen_counter_finish)) {
        errors.kitchen_counter_finish = 'Please select the kitchen counter finish.';
      }
      if (isBlank(data.secondary_flooring_type)) {
        errors.secondary_flooring_type = 'Please select the primary flooring type.';
      }
      if (isBlank(data.overall_interior_condition)) {
        errors.overall_interior_condition = 'Please select the overall interior condition.';
      }
      if (isBlank(data.overall_exterior_condition)) {
        errors.overall_exterior_condition = 'Please select the overall exterior condition.';
      }
      if (isBlank(data.close_to_high_voltage_lines)) {
        errors.close_to_high_voltage_lines = 'Please indicate if the property is close to high voltage lines.';
      }
      if (isBlank(data.property_sloped)) {
        errors.property_sloped = 'Please indicate if the property is sloped.';
      }
      break;

    case MARKETABILITY_ASSESSMENT:
      if (isBlank(data.price_suggestion)) {
        errors.price_suggestion = 'Please provide your price suggestion.';
      } else if (data.price_suggestion.startsWith('No')) {
        if (isBlank(data.suggested_price)) {
          errors.suggested_price = 'Please provide your suggested price.';
        }
      }
      break;

    case COMPARABLE_GATE:
      if (isBlank(data.comp_method)) {
        errors.comp_method = 'Please select a comparable selection method.';
      }
      if (data.comp_method === 'market' && isBlank(data.market_advisor_id)) {
        errors.market_advisor_id = 'Please enter your market advisor ID.';
      }
      break;

    case COMPARABLE_1:
      if (isBlank(data.comp_1_address)) {
        errors.comp_1_address = 'Field is required.';
      }
      if (isBlank(data.comp_1_mls_id)) {
        errors.comp_1_mls_id = 'Field is required.';
      }
      if (isBlank(data.comp_1_condition)) {
        errors.comp_1_condition = 'Field is required.';
      }
      break;

    case UTILITIES:
      if (isBlank(data.utilities_functional)) {
        errors.utilities_functional = 'Please indicate if all utilities are functional.';
      } else if (data.utilities_functional === 'No' || data.utilities_functional === 'Partial') {
        if (isBlank(data.utility_details)) {
          errors.utility_details = 'Please provide details about the utility issues.';
        }
      }
      break;

    case STANDARD_UTILITIES:
      (['electricity', 'water'] as const).forEach((key) => {
        if (isBlank(data[`${key}_on_functional`] as string)) {
          errors[`${key}_on_functional`] = `Please indicate whether the ${key} is on and functional.`;
        }
        if (isBlank(data[`${key}_verification`] as string)) {
          errors[`${key}_verification`] = `Please describe how you verified the status of the ${key}.`;
        }
      });
      // Gas: existence gate, then status + verification only if the home has gas.
      if (isBlank(data.has_gas)) {
        errors.has_gas = 'Please indicate whether the home utilizes gas.';
      } else if (data.has_gas === 'Yes') {
        if (isBlank(data.gas_on_functional)) {
          errors.gas_on_functional = 'Please indicate whether the gas is on and functional.';
        }
        if (isBlank(data.gas_verification)) {
          errors.gas_verification = 'Please describe how you verified the status of the gas.';
        }
      }
      break;

    case SPARKLE_CLEAN:
      if (isBlank(data.sparkle_needed)) {
        errors.sparkle_needed = 'Please indicate whether this home needs a sparkle clean.';
      }
      if (isBlank(data.sparkle_added)) {
        errors.sparkle_added = 'Please indicate whether you added a Sparkle Clean to this visit.';
      } else if (data.sparkle_added === 'Yes') {
        if (isBlank(data.sparkle_reasons)) {
          errors.sparkle_reasons = 'Please select at least one reason.';
        } else if ((data.sparkle_reasons as string).split(',').includes('Other') && isBlank(data.sparkle_reason_other)) {
          errors.sparkle_reason_other = 'Please describe the other reason.';
        }
      } else if (data.sparkle_added === 'No') {
        if (isBlank(data.sparkle_no_reason)) {
          errors.sparkle_no_reason = 'Please indicate why a Sparkle Clean was not added.';
        } else if (data.sparkle_no_reason === 'Other' && isBlank(data.sparkle_no_reason_other)) {
          errors.sparkle_no_reason_other = 'Please describe the other reason.';
        }
      }
      break;

    default:
      break;
  }

  return errors;
}
